import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { User, newUser, Email, Phone, Address, newEmail, newPhone, newAddress, Company, newCompany } from "..";
import { ClickableIcon } from "../../components/ui/Icon";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CustomPhoneInput from "../../components/ui/CustomPhoneInput";
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'
import { SelectServerSearch } from "../../components/ui/DropdownSelect";
import { SingleDatePicker } from "../../components/ui/SingleDatePicker";

interface CompanyFormProps {
  item: Company;
  error?: string;
}

export interface CompanyFormRef {
  valid: () => boolean;
  getItem: () => Company;
}

export const CompanyForm = forwardRef((props: CompanyFormProps, ref) => {
  const { error } = props;
  const [item, setItem] = useState<Company>(newCompany);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setItem(props.item);
  }, [props.item]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(item) as (keyof Company)[]) {
      if (typeof item[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getItem = () => {
    return item;
  }

  // Expose the valid function to the parent, and a method to get the whole item object
  useImperativeHandle(ref, () => ({
    valid,
    getItem,
  }));

  const validateBaseField = (field: keyof Company): string => {
    if (field === "name") {
      if (!item.name?.trim()) return "Navn kan ikke være tom";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof Company, value: any) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  const handleBaseBlur = (field: keyof Company) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <TextField
            label="Navn"
            required
            fullWidth
            value={item.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Organisasjonsnummer"
            fullWidth
            value={item.organization_number}
            onChange={(e) => handleBaseChange("organization_number", e.target.value)}
            variant="outlined"
            error={validateBaseField("organization_number") !== "" && (submitted || touched.base.organization_number)}
            helperText={(submitted || touched.base.organization_number) ? validateBaseField("organization_number") : ""}
            onBlur={() => handleBaseBlur("organization_number")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="brreg_parent_id"
            fullWidth
            value={item.brreg_parent_id}
            onChange={(e) => handleBaseChange("brreg_parent_id", e.target.value)}
            variant="outlined"
            type={"number"}
            error={validateBaseField("brreg_parent_id") !== "" && (submitted || touched.base.brreg_parent_id)}
            helperText={(submitted || touched.base.brreg_parent_id) ? validateBaseField("brreg_parent_id") : ""}
            onBlur={() => handleBaseBlur("brreg_parent_id")}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!item.salary_deduction_instance}
                onChange={(e) => handleBaseChange("salary_deduction_instance", e.target.checked)}
              />
            }
            label="Lønnstrekk instans"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!item.allow_employments}
                onChange={(e) => handleBaseChange("allow_employments", e.target.checked)}
              />
            }
            label="Tillat ansatte"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectServerSearch
            label="Primær kontakt"
            tableName="contact"
            selected={item.primary_contact_id ? [item.primary_contact_id] : []}
            singleSelect={true}
            onChange={(ids) => {
              if (ids.length > 0) {
                handleBaseChange("primary_contact_id", ids[0]);
              } else {
                handleBaseChange("primary_contact_id", null);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectServerSearch
            label="Bedriftsgruppe"
            tableName="company_group"
            selected={item.company_group_id ? [item.company_group_id] : []}
            singleSelect={true}
            onChange={(ids) => {
              if (ids.length > 0) {
                handleBaseChange("company_group_id", ids[0]);
              } else {
                handleBaseChange("company_group_id", null);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Ekstern ID"
            fullWidth
            value={item.external_id}
            onChange={(e) => handleBaseChange("external_id", e.target.value)}
            variant="outlined"
          />
        </Grid>
      </Grid>
    </form>
  )
});